import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { useStore } from "@tanstack/react-form";
import React, { useState, useEffect } from "react";
import { NumericInput } from "@/components/ui/numeric-input";
import { calculateGangRunAreaWeights } from "@apv-erp/status";

function OrderDetailSelector({
  salesOrderId,
  value,
  valueLabel,
  onChange,
}: {
  salesOrderId?: string;
  value: string;
  valueLabel?: string;
  onChange: (val: string, detail?: any) => void;
}) {
  const { data: order, isLoading } = useQuery({
    queryKey: ["orders-getById", salesOrderId],
    queryFn: () => trpcClient.orders.getById.query({ id: salesOrderId! }),
    enabled: !!salesOrderId,
    staleTime: 60000,
  });

  const details = order?.orderDetails || [];

  // Lọc ra các chi tiết đơn hàng chưa tạo lệnh đủ số lượng, hoặc là dòng hiện tại đang chọn
  const filteredDetails = details.filter(
    (d: any) => d.id === value || d.quantity > (d.lsxQty || 0),
  );

  const optionsList = filteredDetails.length > 0 ? filteredDetails : details;

  let options = optionsList.map((d: any) => ({
    id: d.id,
    label: d.productName || "—",
    subLabel: d.detailId || d.productCode,
    meta: `Cần Lệnh: ${Math.max(0, d.quantity - (d.lsxQty || 0))} | Đặt: ${d.quantity} | Đã tạo lệnh: ${d.lsxQty || 0}`,
    original: d,
  }));

  if (value && !options.some((o: any) => o.id === value)) {
    options = [
      {
        id: value,
        label: valueLabel || "Chi tiết đang chọn",
        subLabel: "",
        meta: "",
        original: null,
      },
      ...options,
    ];
  }

  return (
    <ComboboxFK
      value={value}
      valueLabel={valueLabel}
      onChange={(id, opt) => onChange(id, opt?.original)}
      options={options}
      loading={isLoading}
      placeholder={
        salesOrderId
          ? "Chọn chi tiết sản phẩm..."
          : "Vui lòng chọn đơn hàng trước"
      }
      className="h-9"
    />
  );
}

function OrderSourceRow({
  item,
  idx,
  updateRow,
  removeRow,
  customerId,
  onAutoSetCustomer,
  costWeightPercent,
}: {
  item: any;
  idx: number;
  updateRow: (idx: number, newItem: any) => void;
  removeRow: (idx: number) => void;
  customerId?: string;
  onAutoSetCustomer?: (cId: string, cName?: string) => void;
  costWeightPercent?: number;
}) {
  const salesOrderId = item.salesOrderId;
  const orderDetailId = item.orderDetailId;

  const { data: order } = useQuery({
    queryKey: ["orders-getById", salesOrderId],
    queryFn: () => trpcClient.orders.getById.query({ id: salesOrderId! }),
    enabled: !!salesOrderId,
    staleTime: 60000,
  });

  // Auto-set customer if not yet selected on the form
  useEffect(() => {
    if (order?.customerId && onAutoSetCustomer) {
      onAutoSetCustomer(order.customerId, order.customer?.name || order.customer?.shortName);
    }
  }, [order?.customerId, onAutoSetCustomer]);

  const detail = order?.orderDetails?.find((d: any) => d.id === orderDetailId);

  const orderedQty = item.quantity || (detail ? detail.quantity : 0);
  const producedQty = item.finishedQty ?? detail?.producedQty ?? 0;
  const stockQty = item.inventoryQty ?? 0;
  const planQty = item.plannedQty !== undefined ? item.plannedQty : Math.ceil(orderedQty * 1.05);
  const ups = item.pcsPerSheet || item.ups || detail?.product?.outs || 1;
  const reqSheets = ups > 0 ? Math.ceil(planQty / ups) : planQty;
  const progressPercent = item.progressPercent ?? (planQty > 0 ? Math.min(100, Math.round((producedQty / planQty) * 100)) : 0);
  const remainingQty = orderedQty - producedQty;

  return (
    <TableRow>
      <TableCell className="text-center font-mono font-bold text-xs text-slate-400">
        {idx + 1}
      </TableCell>
      <TableCell>
        <SharedCombobox
          type="order"
          value={item.salesOrderId || ""}
          valueLabel={item.salesOrderNumber || (item.salesOrderId ? `ĐH ${item.salesOrderId}` : undefined)}
          onChange={(id, opt) => {
            updateRow(idx, {
              ...item,
              salesOrderId: id,
              salesOrderNumber: opt?.label || item.salesOrderNumber,
              orderDetailId: "",
            });
            if (opt?.original?.customerId && onAutoSetCustomer) {
              onAutoSetCustomer(opt.original.customerId, opt.original.customer?.name || opt.original.customerName);
            }
          }}
          placeholder="Chọn đơn hàng..."
          params={{ customerId: customerId || undefined, forPlanning: true }}
        />
      </TableCell>
      <TableCell>
        <OrderDetailSelector
          salesOrderId={salesOrderId}
          value={item.orderDetailId || ""}
          valueLabel={item.productName || (detail ? detail.productName : undefined)}
          onChange={(id, selectedDetail) => {
            const qty = selectedDetail ? selectedDetail.quantity : (detail?.quantity || item.quantity || 0);
            const defPlan = Math.ceil(qty * 1.05);
            const defUps = selectedDetail?.product?.outs || selectedDetail?.outs || selectedDetail?.prepressUps || item.pcsPerSheet || 1;
            const pCode = selectedDetail?.productCode || selectedDetail?.product?.productCode || item.productCode || "";
            const pName = selectedDetail?.productName || selectedDetail?.product?.productName || item.productName || "";
            const pId = selectedDetail?.productId || selectedDetail?.product?.id || item.productId;
            const pProcs = selectedDetail?.product?.productProcesses || selectedDetail?.productProcesses || [];
            const pMats = selectedDetail?.product?.productMaterials || selectedDetail?.productMaterials || [];
            const flatL = selectedDetail?.product?.flatLength || selectedDetail?.product?.length || item.flatLength || 100;
            const flatW = selectedDetail?.product?.flatWidth || selectedDetail?.product?.width || item.flatWidth || 100;
            updateRow(idx, {
              ...item,
              orderDetailId: id,
              productName: pName,
              productCode: pCode,
              productId: pId,
              quantity: qty,
              plannedQty: item.plannedQty || defPlan,
              pcsPerSheet: defUps,
              ups: defUps,
              flatLength: flatL,
              flatWidth: flatW,
              productProcesses: pProcs,
              productMaterials: pMats,
              detail: selectedDetail,
            });
          }}
        />
      </TableCell>
      <TableCell className="text-right font-mono font-semibold text-slate-700 text-xs">
        {orderedQty > 0 ? orderedQty.toLocaleString("vi-VN") : "-"}
      </TableCell>
      <TableCell className="text-right font-mono font-medium text-emerald-600 text-xs">
        {stockQty > 0 ? stockQty.toLocaleString("vi-VN") : "0"}
      </TableCell>
      <TableCell>
        <NumericInput
          value={item.plannedQty !== undefined ? item.plannedQty : (item.quantity || "")}
          onChange={(e) =>
            updateRow(idx, { ...item, plannedQty: Number(e.target.value) })
          }
          placeholder="SL Kế hoạch"
          className="h-8 font-mono text-right text-xs"
        />
      </TableCell>
      <TableCell>
        <NumericInput
          value={item.pcsPerSheet !== undefined ? item.pcsPerSheet : (item.ups || 1)}
          onChange={(e) => {
            const newUps = Math.max(1, Number(e.target.value));
            updateRow(idx, { ...item, pcsPerSheet: newUps, ups: newUps });
          }}
          placeholder="UPS"
          className="h-8 font-mono text-right text-xs w-16"
        />
      </TableCell>
      <TableCell className="text-right font-mono font-semibold text-indigo-900 text-xs">
        {reqSheets.toLocaleString("vi-VN")}
      </TableCell>
      <TableCell className="text-center font-mono text-xs" data-testid={`order-source-area-weight-${idx}`}>
        <span
          className="px-1.5 py-0.5 rounded text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200"
          title="Tỷ trọng phân bổ giá thành giấy & công in theo diện tích chiếm chỗ (UPS × Dài × Rộng)"
        >
          {costWeightPercent !== undefined ? `${costWeightPercent}%` : "100%"}
        </span>
      </TableCell>
      <TableCell className="text-right font-mono text-slate-600 text-xs">
        {Math.max(0, remainingQty).toLocaleString("vi-VN")}
      </TableCell>
      <TableCell className="text-center font-mono text-xs">
        <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-indigo-50 text-indigo-700">
          {progressPercent}%
        </span>
      </TableCell>
      <TableCell className="text-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
          onClick={() => removeRow(idx)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

export function OrderSourceBuilder({ form }: { form: any }) {
  const orderSources =
    useStore(form.store, (s: any) => s.values.orderSources) || [];
  const customerId = useStore(form.store, (s: any) => s.values.customerId);

  const handleAutoSetCustomer = React.useCallback(
    (cId: string, cName?: string) => {
      const currentCId = form.getFieldValue("customerId");
      if (!currentCId && cId) {
        form.setFieldValue("customerId", cId);
        if (cName) {
          form.setFieldValue("customerName", cName);
        }
      }
    },
    [form]
  );

  const updateSources = (newSources: any[]) => {
    form.setFieldValue("orderSources", newSources);
  };

  const addRow = () => {
    const current = form.getFieldValue("orderSources") || [];
    updateSources([
      ...current,
      { salesOrderId: "", orderDetailId: "", quantity: 0, plannedQty: 0, pcsPerSheet: 1, inventoryQty: 0 },
    ]);
  };

  const updateRow = (idx: number, newItem: any) => {
    const current = form.getFieldValue("orderSources") || [];
    const newSources = [...current];
    newSources[idx] = newItem;
    updateSources(newSources);
  };

  const removeRow = (idx: number) => {
    const current = form.getFieldValue("orderSources") || [];
    const newSources = current.filter((_: any, i: number) => i !== idx);
    updateSources(newSources);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold text-slate-700">
          Nguồn lệnh ghép bài & Đơn hàng (Order Sources)
        </h4>
        <Button type="button" size="sm" variant="outline" onClick={addRow}>
          <Plus className="h-4 w-4 mr-2" /> Thêm nguồn
        </Button>
      </div>

      <div className="border rounded-md overflow-hidden bg-white shadow-sm">
        <form.Subscribe selector={(state: any) => state.values.orderSources}>
          {(orderSources: any[]) => {
            const items = orderSources || [];
            const areaWeights = calculateGangRunAreaWeights(
              items.map((it: any, idx: number) => ({
                itemId: String(it.orderDetailId || idx),
                ups: Number(it.pcsPerSheet || it.ups || 1),
                flatLengthMm: Number(it.flatLength || it.detail?.product?.flatLength || it.detail?.product?.length || 100),
                flatWidthMm: Number(it.flatWidth || it.detail?.product?.flatWidth || it.detail?.product?.width || 100),
              })),
            );
            return (
              <Table>
                <TableHeader className="bg-slate-50/50">
                  <TableRow>
                    <TableHead className="w-8 text-center text-xs">
                      #
                    </TableHead>
                    <TableHead className="w-[160px] text-xs">
                      Mã đơn hàng
                    </TableHead>
                    <TableHead className="min-w-[180px] text-xs">
                      Sản phẩm đơn hàng (Chi tiết)
                    </TableHead>
                    <TableHead className="w-[85px] text-right text-xs">
                      SL Đặt
                    </TableHead>
                    <TableHead className="w-[85px] text-right text-xs text-emerald-700">
                      Tồn FG
                    </TableHead>
                    <TableHead className="w-[110px] text-right text-xs">
                      SL Kế hoạch
                    </TableHead>
                    <TableHead className="w-[75px] text-right text-xs">
                      Số con (UPS)
                    </TableHead>
                    <TableHead className="w-[85px] text-right text-xs">
                      Tờ in cần
                    </TableHead>
                    <TableHead className="w-[90px] text-center text-xs text-purple-700 font-semibold" title="Tỷ trọng phân bổ giá thành theo Diện tích × UPS">
                      % Giá thành
                    </TableHead>
                    <TableHead className="w-[85px] text-right text-xs">
                      Còn lại
                    </TableHead>
                    <TableHead className="w-[70px] text-center text-xs">
                      Tiến độ
                    </TableHead>
                    <TableHead className="w-[45px] text-center text-xs">
                      Thao tác
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        className="text-center text-slate-500 py-8 text-xs"
                      >
                        Chưa có nguồn lệnh nào. Nhấn "Thêm nguồn" để ghép bài đơn hàng.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item: any, idx: number) => (
                      <React.Fragment key={idx}>
                        <OrderSourceRow
                          idx={idx}
                          item={item}
                          updateRow={updateRow}
                          removeRow={removeRow}
                          customerId={customerId}
                          onAutoSetCustomer={handleAutoSetCustomer}
                          costWeightPercent={areaWeights[idx]?.weightPercent ?? 100}
                        />
                        {/* Ghi chú theo dòng sản phẩm (thay ghi chú giao hàng / thành phẩm của khối thông số gia công cũ) */}
                        <TableRow data-testid="order-source-notes-row" className="bg-slate-50/40 hover:bg-slate-50/40">
                          <TableCell />
                          <TableCell colSpan={11} className="py-1.5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <Input
                                data-testid="order-source-delivery-notes"
                                aria-label="Ghi chú giao hàng"
                                value={item.deliveryNotes || ""}
                                onChange={(e) => updateRow(idx, { ...item, deliveryNotes: e.target.value })}
                                placeholder="Ghi chú giao hàng: VD 500 cái/thùng, đóng đai, dán nhãn..."
                                className="h-8 text-xs bg-white"
                              />
                              <Input
                                data-testid="order-source-finishing-notes"
                                aria-label="Ghi chú thành phẩm"
                                value={item.finishingNotes || ""}
                                onChange={(e) => updateRow(idx, { ...item, finishingNotes: e.target.value })}
                                placeholder="Ghi chú thành phẩm: VD kiểm tra dính keo, lau sạch bụi..."
                                className="h-8 text-xs bg-white"
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            );
          }}
        </form.Subscribe>
      </div>
    </div>
  );
}
